import React, { useState, memo } from 'react';
import { ArrowLeft, Share2, Bookmark, MoreVertical, X } from 'lucide-react';
import { BlogPost } from '@/services/blogService';
import { TouchButton, useResponsive } from './ResponsiveUtils';
import { AccessibleButton, useFocusTrap } from './AccessibilityUtils';

interface MobileNavigationProps {
  post: BlogPost;
  onBack?: () => void;
  className?: string;
}

export const MobileNavigation: React.FC<MobileNavigationProps> = memo(({
  post,
  onBack,
  className = ''
}) => {
  const [showActions, setShowActions] = useState(false);
  const { isMobile } = useResponsive();
  const modalRef = useFocusTrap(showActions);

  if (!isMobile) return null;

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: post.title,
          text: post.excerpt,
          url: window.location.href,
        });
      } catch (error) {
        // User cancelled or error occurred
        console.log('Share cancelled or failed');
      }
    } else {
      // Fallback to clipboard
      await navigator.clipboard.writeText(window.location.href);
      // Could show toast notification here
    }
    setShowActions(false);
  };

  const handleBookmark = () => {
    // Implement bookmark functionality
    console.log('Bookmark post:', post.slug);
    setShowActions(false);
  };

  return (
    <>
      {/* Fixed Mobile Navigation Bar */}
      <nav 
        className={`fixed top-0 left-0 right-0 z-40 bg-slate-900/80 backdrop-blur-xl border-b border-white/10 ${className}`}
        role="navigation"
        aria-label="Mobile blog navigation"
      >
        <div className="flex items-center justify-between px-4 py-3">
          {/* Back Button */}
          <TouchButton
            onClick={onBack || (() => window.history.back())}
            className="text-white/70 hover:text-white p-2"
            size="sm"
          >
            <ArrowLeft className="w-5 h-5" />
          </TouchButton>

          {/* Title - Truncated */}
          <div className="flex-1 mx-4 min-w-0">
            <h1 className="text-white font-medium text-sm truncate">
              {post.title}
            </h1>
          </div>

          {/* Actions Button */}
          <TouchButton
            onClick={() => setShowActions(true)}
            className="text-white/70 hover:text-white p-2"
            size="sm"
          >
            <MoreVertical className="w-5 h-5" />
          </TouchButton>
        </div>
      </nav>

      {/* Actions Modal */}
      {showActions && (
        <div className="fixed inset-0 z-50 overflow-hidden">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowActions(false)}
          />
          
          {/* Modal Content */}
          <div 
            ref={modalRef}
            className="absolute bottom-0 left-0 right-0 bg-slate-900/95 backdrop-blur-xl border-t border-white/10 rounded-t-2xl"
            role="dialog"
            aria-modal="true"
            aria-labelledby="actions-title"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <h3 id="actions-title" className="text-white font-semibold">
                Actions
              </h3>
              <AccessibleButton
                onClick={() => setShowActions(false)}
                ariaLabel="Close actions menu"
                variant="ghost"
                className="p-2"
              >
                <X className="w-5 h-5" />
              </AccessibleButton>
            </div>

            {/* Action Items */}
            <div className="p-4 space-y-2">
              <TouchButton
                onClick={handleShare}
                className="w-full flex items-center gap-3 p-4 text-left text-white/80 hover:text-white hover:bg-white/10 rounded-xl transition-colors"
                size="md"
              >
                <Share2 className="w-5 h-5 text-blue-400" />
                <div>
                  <div className="font-medium">Share Article</div>
                  <div className="text-sm text-white/60">Share this post with others</div>
                </div>
              </TouchButton>

              <TouchButton
                onClick={handleBookmark}
                className="w-full flex items-center gap-3 p-4 text-left text-white/80 hover:text-white hover:bg-white/10 rounded-xl transition-colors"
                size="md"
              >
                <Bookmark className="w-5 h-5 text-purple-400" />
                <div>
                  <div className="font-medium">Bookmark</div>
                  <div className="text-sm text-white/60">Save for later reading</div>
                </div>
              </TouchButton>
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-white/10 bg-slate-800/50">
              <p className="text-white/60 text-xs text-center">
                Swipe down to close
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
});

MobileNavigation.displayName = 'MobileNavigation';

export default MobileNavigation;